import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  Link2,
  Unlink,
  Building2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

const ERPVinculacion = () => {
  const { token } = useAuth();

  // Bind clients state
  const [bindSearch, setBindSearch] = useState("");
  const [bindClients, setBindClients] = useState([]);
  const [bindLoading, setBindLoading] = useState(false);
  const [bindSearched, setBindSearched] = useState(false);

  // Internal clients state
  const [internalClients, setInternalClients] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [internalFilter, setInternalFilter] = useState("");

  // Linking dialog
  const [linkDialog, setLinkDialog] = useState(false);
  const [selectedBind, setSelectedBind] = useState(null);
  const [selectedInternal, setSelectedInternal] = useState(null);
  const [linking, setLinking] = useState(false);

  // Unlink confirm
  const [unlinkDialog, setUnlinkDialog] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  useEffect(() => {
    fetchInternalClients();
  }, []);

  const fetchInternalClients = async () => {
    setInternalLoading(true);
    try {
      const res = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInternalClients(res.data);
    } catch (err) {
      console.error("Error fetching internal clients:", err);
      toast.error("Error al cargar clientes del sistema");
    } finally {
      setInternalLoading(false);
    }
  };

  const searchBindClients = async () => {
    if (!bindSearch.trim()) return;
    setBindLoading(true);
    setBindSearched(true);
    try {
      const res = await axios.get(`${API}/erp/clients`, {
        params: { search: bindSearch, page_size: 30 },
        withCredentials: true,
      });
      setBindClients(res.data.items || []);
    } catch (err) {
      console.error("Error searching Bind clients:", err);
      toast.error("Error al buscar en Bind ERP");
      setBindClients([]);
    } finally {
      setBindLoading(false);
    }
  };

  const openLinkDialog = (bindClient) => {
    setSelectedBind(bindClient);
    setSelectedInternal(null);
    setLinkDialog(true);
  };

  const confirmLink = async () => {
    if (!selectedBind || !selectedInternal) return;
    setLinking(true);
    try {
      await axios.post(
        `${API}/erp/vincular-cliente`,
        {
          internal_client_id: selectedInternal.id,
          bind_client_id: selectedBind.ID || selectedBind.ClientID || selectedBind.id,
          bind_client_name: selectedBind.ClientName || selectedBind.Name || "",
        },
        { withCredentials: true }
      );
      toast.success("Cliente vinculado exitosamente");
      setLinkDialog(false);
      fetchInternalClients();
    } catch (err) {
      console.error("Error linking client:", err);
      toast.error("Error al vincular cliente");
    } finally {
      setLinking(false);
    }
  };

  const openUnlinkDialog = (client) => {
    setUnlinkTarget(client);
    setUnlinkDialog(true);
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget) return;
    setLinking(true);
    try {
      await axios.post(
        `${API}/erp/desvincular-cliente`,
        { internal_client_id: unlinkTarget.id },
        { withCredentials: true }
      );
      toast.success("Cliente desvinculado");
      setUnlinkDialog(false);
      setUnlinkTarget(null);
      fetchInternalClients();
    } catch (err) {
      console.error("Error unlinking client:", err);
      toast.error("Error al desvincular");
    } finally {
      setLinking(false);
    }
  };

  // Linked and unlinked clients
  const linkedClients = internalClients.filter((c) => c.bind_client_id);
  const unlinkedClients = internalClients.filter((c) => !c.bind_client_id);

  // Filter for internal clients in the dialog
  const dialogFilteredClients = unlinkedClients.filter((c) => {
    const q = internalFilter.toLowerCase();
    return (
      !q ||
      c.company_name?.toLowerCase().includes(q) ||
      c.rfc?.toLowerCase().includes(q) ||
      c.contact_name?.toLowerCase().includes(q)
    );
  });

  // Check if a Bind client is already linked
  const isBindLinked = (bindClient) => {
    const bindId = bindClient.ID || bindClient.ClientID || bindClient.id;
    return linkedClients.some((c) => c.bind_client_id === bindId);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800" data-testid="vinculacion-title">
          Vinculación de Clientes con Bind ERP
        </h1>
        <p className="text-slate-500 mt-1">
          Busca clientes en Bind ERP y vincúlalos con los clientes del sistema para facturación
        </p>
      </div>

      {/* Resumen de vinculación */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{internalClients.length}</p>
                <p className="text-sm text-slate-500">Clientes totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{linkedClients.length}</p>
                <p className="text-sm text-slate-500">Vinculados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{unlinkedClients.length}</p>
                <p className="text-sm text-slate-500">Sin vincular</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Buscador Bind ERP */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="w-5 h-5 text-blue-600" />
            Buscar Clientes en Bind ERP
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por nombre de cliente en Bind..."
              value={bindSearch}
              onChange={(e) => setBindSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchBindClients()}
              className="flex-1"
              data-testid="bind-search-input"
            />
            <Button onClick={searchBindClients} disabled={bindLoading} data-testid="bind-search-btn">
              {bindLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-2">Buscar</span>
            </Button>
          </div>

          {bindSearched && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre en Bind</TableHead>
                    <TableHead>RFC</TableHead>
                    <TableHead>ID Bind</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead className="w-[120px]">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bindClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                        {bindLoading ? "Buscando..." : "No se encontraron clientes en Bind"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    bindClients.map((bc) => {
                      const bindId = bc.ID || bc.ClientID || bc.id || "";
                      const alreadyLinked = isBindLinked(bc);
                      return (
                        <TableRow key={bindId}>
                          <TableCell className="font-medium">{bc.ClientName || bc.Name || "—"}</TableCell>
                          <TableCell className="font-mono text-sm">{bc.RFC || "—"}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-500">
                            {bindId.substring(0, 12)}...
                          </TableCell>
                          <TableCell>
                            {alreadyLinked ? (
                              <Badge className="bg-green-100 text-green-700 border border-green-300">
                                Ya vinculado
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-slate-100 text-slate-500">
                                Disponible
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant={alreadyLinked ? "ghost" : "default"}
                              disabled={alreadyLinked}
                              onClick={() => openLinkDialog(bc)}
                              data-testid={`link-bind-${bindId.substring(0, 8)}`}
                            >
                              <Link2 className="w-4 h-4 mr-1" />
                              {alreadyLinked ? "Vinculado" : "Vincular"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clientes vinculados */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Clientes Vinculados ({linkedClients.length})
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchInternalClients} data-testid="refresh-clients-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {linkedClients.length === 0 ? (
            <p className="text-center text-slate-400 py-6">
              No hay clientes vinculados aún. Busca un cliente en Bind y vincúlalo.
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente del Sistema</TableHead>
                    <TableHead>RFC</TableHead>
                    <TableHead>Cliente Bind</TableHead>
                    <TableHead>ID Bind</TableHead>
                    <TableHead className="w-[120px]">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedClients.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.company_name}</TableCell>
                      <TableCell className="font-mono text-sm">{c.rfc}</TableCell>
                      <TableCell className="text-sm">{c.bind_client_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {c.bind_client_id?.substring(0, 12)}...
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openUnlinkDialog(c)}
                          data-testid={`unlink-${c.id}`}
                        >
                          <Unlink className="w-4 h-4 mr-1" />
                          Desvincular
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Seleccionar cliente interno para vincular */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Vincular Cliente de Bind
            </DialogTitle>
            <DialogDescription>
              Selecciona el cliente del sistema que corresponde a{" "}
              <strong>{selectedBind?.ClientName || selectedBind?.Name || ""}</strong> en Bind ERP
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info del cliente Bind seleccionado */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-800">Cliente en Bind:</p>
              <p className="text-sm text-blue-700">
                {selectedBind?.ClientName || selectedBind?.Name} | RFC: {selectedBind?.RFC || "N/A"}
              </p>
            </div>

            {/* Buscador de clientes internos */}
            <Input
              placeholder="Filtrar clientes del sistema por nombre, RFC..."
              value={internalFilter}
              onChange={(e) => setInternalFilter(e.target.value)}
              data-testid="internal-filter-input"
            />

            {/* Lista de clientes internos no vinculados */}
            <div className="border rounded-lg overflow-auto max-h-[40vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>RFC</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead className="w-[100px]">Seleccionar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dialogFilteredClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-400 py-6">
                        No hay clientes sin vincular que coincidan
                      </TableCell>
                    </TableRow>
                  ) : (
                    dialogFilteredClients.map((c) => (
                      <TableRow
                        key={c.id}
                        className={`cursor-pointer transition-colors ${
                          selectedInternal?.id === c.id
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : "hover:bg-slate-50"
                        }`}
                        onClick={() => setSelectedInternal(c)}
                      >
                        <TableCell className="font-medium">{c.company_name}</TableCell>
                        <TableCell className="font-mono text-sm">{c.rfc}</TableCell>
                        <TableCell className="text-sm">{c.contact_name}</TableCell>
                        <TableCell>
                          {selectedInternal?.id === c.id ? (
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmLink}
              disabled={!selectedInternal || linking}
              data-testid="confirm-link-btn"
            >
              {linking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar desvinculación */}
      <Dialog open={unlinkDialog} onOpenChange={setUnlinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Unlink className="w-5 h-5" />
              Desvincular Cliente
            </DialogTitle>
            <DialogDescription>
              Se desvinculará <strong>{unlinkTarget?.company_name}</strong> de Bind ERP.
              Esto no eliminará ningún dato en Bind, solo se quitará la referencia en el sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlinkDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmUnlink}
              disabled={linking}
              data-testid="confirm-unlink-btn"
            >
              {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlink className="w-4 h-4 mr-2" />}
              Desvincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ERPVinculacion;
